import { AfterViewInit, Component, EventEmitter, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { HotTableRegisterer } from '@handsontable/angular';
import { MatDialogRef } from '@angular/material/dialog';
import Handsontable from 'handsontable';
import { Subscription } from 'rxjs';
import { NegotiationHandsontableService } from 'src/app/core/services/negotiation-handsontable.service';
import { FormData } from 'src/app/shared/models/form-data.model';
import { NegotiationFormTemplateComponent } from './form-template/negotiation-form-template.component';
import { NegotiationDialogApi } from 'src/app/core/services/negotiation-dialog-api';
import { negotiationColumnWidths } from './negotiation-table-layout';

@Component({
    selector: 'app-negotiation-form',
    templateUrl: './negotiation-form.component.html',
    styleUrls: ['./negotiation-form.component.scss'],
    providers: [NegotiationHandsontableService]
})
export class NegotiationFormComponent implements AfterViewInit, OnDestroy {
    @ViewChild('formTemplate', { static: true }) private formTemplate: NegotiationFormTemplateComponent;
    @Output() formReady = new EventEmitter<void>();
    @Output() formLoadError = new EventEmitter<void>();
    @Output() requiredFieldsCompleteChange = new EventEmitter<boolean>();
    @Output() valuesChange = new EventEmitter<void>();
    public isUpdate = true;
    public editableCustomerIds: string[] = [];
    public referenceApi: NegotiationDialogApi;
    private registerer = new HotTableRegisterer();
    private viewInitialized = false;
    private destroyed = false;
    private loadTimer: number;
    private layoutFrame: number;
    private subscriptions = new Subscription();
    private pendingModel: { data: FormData, users: any[] };
    private observedTable: Handsontable;
    private observedTableElement: HTMLElement;
    private tableWidthObserver: any;
    private lastTableWidth = 0;
    private lastCompactMode = false;
    private responsiveWidths = [45, 150, 220, 140, 150];
    private onTableResize = () => {
        if (this.destroyed || !this.observedTable || this.observedTable.isDestroyed || !this.isIOS()) {
            return;
        }
        const width = this.tableViewportWidth();
        const compact = window.matchMedia('(max-width: 767px)').matches;
        // Keyboard/row height changes alone must not rebuild the table during input.
        if (width > 0 && (width !== this.lastTableWidth || compact !== this.lastCompactMode)) {
            this.refreshTableLayout();
        }
    };
    private onEditorInput = () => {
        if (!this.destroyed) {
            this.notifyValuesChange();
            this.updateRequiredState();
        }
    };
    private lastRequiredState: boolean;
    private initialValues: string;
    private lastValues: string;
    private onTableChange = (changes: any[]) => {
        if (!this.destroyed && changes && changes.some(change => change &&
            (change[1] === 'value' || change[1] === 2))) {
            this.notifyValuesChange();
            this.updateRequiredState();
        }
    };

    constructor(private zone: NgZone, private dialogRef: MatDialogRef<any>) { }

    private isIOS(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    private tableViewportWidth(): number {
        const element = this.observedTableElement;
        if (!element) {
            return 0;
        }
        const holder = element.querySelector('.ht_master .wtHolder') as HTMLElement;
        const scrollbar = holder ? Math.max(0, holder.offsetWidth - holder.clientWidth) : 0;
        return Math.max(0, element.clientWidth - scrollbar - 2);
    }

    private fitTableColumns(): void {
        if (!this.isIOS()) {
            return;
        }
        const width = this.tableViewportWidth();
        const compact = window.matchMedia('(max-width: 767px)').matches;
        if (width <= 0 || (width === this.lastTableWidth && compact === this.lastCompactMode)) {
            return;
        }
        this.lastTableWidth = width;
        this.lastCompactMode = compact;
        this.responsiveWidths = negotiationColumnWidths(width, compact);
        const holder = this.observedTableElement.querySelector('.ht_master .wtHolder');
        if (compact && holder) {
            holder.scrollLeft = 0;
        }
    }

    private notifyValuesChange(): void {
        const current = this.getValuesSnapshot();
        if (current === null) {
            return;
        }
        const previous = this.lastValues;
        this.lastValues = current;
        if (previous !== undefined && previous !== current) {
            this.zone.run(() => this.valuesChange.emit());
        }
    }

    private updateRequiredState(): void {
        const complete = this.formTemplate.hasCompleteRequiredFields();
        if (complete !== this.lastRequiredState) {
            this.lastRequiredState = complete;
            this.zone.run(() => this.requiredFieldsCompleteChange.emit(complete));
        }
    }

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        this.subscriptions.add(this.dialogRef.afterOpened().subscribe(() => this.refreshTableLayout()));
        this.loadModel();
    }

    private refreshTableLayout(): void {
        if (this.destroyed) {
            return;
        }
        window.cancelAnimationFrame(this.layoutFrame);
        this.zone.runOutsideAngular(() => {
            this.layoutFrame = window.requestAnimationFrame(() => {
                this.layoutFrame = null;
                const table = this.observedTable;
                if (this.destroyed || !table || table.isDestroyed) {
                    return;
                }
                this.fitTableColumns();
                table.refreshDimensions();
                table.render();
            });
        });
    }

    setEditableCustomerIds(customerIds: string[]): void {
        this.editableCustomerIds = customerIds || [];
    }

    processDataWithModel(data: FormData, users: any[]): void {
        this.pendingModel = { data, users: users || [] };
        this.loadModel();
    }

    private loadModel(): void {
        if (!this.viewInitialized || !this.pendingModel || this.destroyed) {
            return;
        }
        window.clearTimeout(this.loadTimer);
        this.loadTimer = window.setTimeout(() => {
            if (this.destroyed || !this.pendingModel) {
                return;
            }
            const model = this.pendingModel;
            this.pendingModel = null;
            this.formTemplate.isUpdate = this.isUpdate;
            this.formTemplate.negotiationCustomerIds = this.editableCustomerIds;
            this.formTemplate.referenceApi = this.referenceApi;
            this.formTemplate.processDataWithModel(model.data, model.users);
        });
    }

    onFormReady(): void {
        if (this.destroyed) {
            return;
        }
        const table = this.registerer.getInstance('negotiation-template');
        if (!table) {
            this.formLoadError.emit();
            return;
        }
        table.updateSettings({
            height: '60vh',
            rowHeaders: false,
            colWidths: this.isIOS() ? (column: number) => this.responsiveWidths[column] : [45, 150, 220, 140, 150],
            hiddenColumns: { columns: [4], indicators: false }
        });
        table.render();
        if (this.observedTable !== table) {
            this.lastValues = this.getValuesSnapshot();
            this.observedTable = table;
            this.observedTableElement = table.rootElement;
            this.observedTableElement.addEventListener('input', this.onEditorInput);
            table.addHook('afterChange', this.onTableChange);
            if (this.isIOS()) {
                this.zone.runOutsideAngular(() => {
                    const WidthObserver = (window as any).ResizeObserver;
                    if (WidthObserver) {
                        this.tableWidthObserver = new WidthObserver(this.onTableResize);
                        this.tableWidthObserver.observe(this.observedTableElement);
                    }
                    window.addEventListener('resize', this.onTableResize);
                });
            }
        }
        this.updateRequiredState();
        if (this.initialValues === undefined) {
            this.initialValues = this.formTemplate.getEditableValuesSnapshot();
        }
        this.formReady.emit();
        this.refreshTableLayout();
    }

    hasUnsavedChanges(): boolean {
        if (this.destroyed || this.initialValues === undefined || this.initialValues === null) {
            return false;
        }
        const current = this.formTemplate.getEditableValuesSnapshot();
        return current !== null && current !== this.initialValues;
    }

    validate(callback: (valid: boolean) => void): void {
        this.formTemplate.validateForm(valid => {
            if (!this.destroyed) {
                callback(valid);
            }
        });
    }

    getPreviewFields() {
        return this.formTemplate.getPreviewFields();
    }

    getValuesSnapshot(): string {
        return this.destroyed ? null : this.formTemplate.getEditableValuesSnapshot();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.tableWidthObserver) {
            this.tableWidthObserver.disconnect();
            this.tableWidthObserver = null;
        }
        window.removeEventListener('resize', this.onTableResize);
        window.clearTimeout(this.loadTimer);
        window.cancelAnimationFrame(this.layoutFrame);
        this.subscriptions.unsubscribe();
        this.pendingModel = null;
        if (this.observedTableElement) {
            this.observedTableElement.removeEventListener('input', this.onEditorInput);
            this.observedTableElement = null;
        }
        if (this.observedTable && !this.observedTable.isDestroyed) {
            this.observedTable.removeHook('afterChange', this.onTableChange);
        }
        this.observedTable = null;
    }
}
